'use client';

import React from 'react';
import SVGIcons from '@components/SVGIcon';
import {MuliSelectOption} from '@new-components/molecules/MultiSelectSearchBox';
import {SelectionType} from '@new-components/organisms/SearchPriceFilter';
import {useSearchParams} from 'next/navigation';

export const IconMap = {
  ['location']: <Destination />,
  ['community']: <SkyTerrace />,
  ['recent']: <SVGIcons type='clock-svg' />,
  ['penthouse']: <Penthouse />,
  ['apartment']: <ApartMent />,
  ['skyterrace']: <SkyTerrace />,
  ['townhouse']: <TownHouse />,
  ['villa']: <Villa />,
  ['virtual']: <Default />,
  ['plot']: <Default/>,
  ['default']: <Default />,
};

export const VALID_SEARCH_PARAMS = [
  'mpr',
  'spr',
  'minar',
  'maxar',
  'cst',
  'bhr',
  'bdr',
  'text',
  'ty',
  'uv',
  'rst',
  'dst',
];
export type SearchState = {
  searchText: MuliSelectOption<keyof typeof IconMap>[]; // dst text
  bedrooms: string[]; // bdr
  bathrooms: string[]; //bhr
  price: SelectionType; //mpr spr
  area: SelectionType; //minar maxar
  propertyType: string[]; // ty
  availability: string; //cst
  propertyView: string; //uv
  saleType: string; //rst
};

export const INITIAL_STATE: SearchState = {
  searchText: [],
  bedrooms: [],
  bathrooms: [],
  price: {
    max: null,
    min: null,
  },
  area: {
    max: null,
    min: null,
  },
  propertyType: [],
  availability: 'Any',
  propertyView: 'Any',
  saleType: 'Any',
};

export type SearchReducerProps = {
  type:
    | 'SET_SEARCH_TEXT'
    | 'SET_BEDROOMS'
    | 'SET_BATHROOMS'
    | 'SET_PRICE'
    | 'SET_AREA'
    | 'SET_PROPERTY_TYPE'
    | 'SET_AVAILABILITY'
    | 'SET_PROPERTY_VIEW'
    | 'SET_SALE_TYPE'
    | 'RESET_STATE';
  payload: any;
};
function Reducer(state: SearchState, action: SearchReducerProps): SearchState {
  switch (action.type) {
    case 'SET_SEARCH_TEXT':
      return {
        ...state,
        searchText: action.payload,
      };
    case 'SET_BEDROOMS':
      return {
        ...state,
        bedrooms: action.payload,
      };
    case 'SET_BATHROOMS':
      return {
        ...state,
        bathrooms: action.payload,
      };
    case 'SET_PRICE':
      return {
        ...state,
        price: {...action.payload},
      };
    case 'SET_AREA':
      return {
        ...state,
        area: {...action.payload},
      };
    case 'SET_PROPERTY_TYPE':
      return {
        ...state,
        propertyType: action.payload,
      };
    case 'SET_AVAILABILITY':
      return {
        ...state,
        availability: action.payload,
      };
    case 'SET_PROPERTY_VIEW':
      return {
        ...state,
        propertyView: action.payload,
      };
    case 'SET_SALE_TYPE':
      return {
        ...state,
        saleType: action.payload,
      };
    case 'RESET_STATE':
      if (action.payload === undefined) return INITIAL_STATE;
      return {...state, ...action.payload};
    default:
      return {...state};
  }
}

export const useSearchState = (
  initialState?: SearchState
): [SearchState, React.Dispatch<SearchReducerProps>] => {
  const [state, dispatch] = React.useReducer(
    Reducer,
    initialState ? initialState : INITIAL_STATE
  );
  const searchParams = useSearchParams();

  React.useEffect(() => {
    dispatch({type: 'RESET_STATE', payload: initialState});
  }, [initialState]);

  React.useEffect(() => {
    const searchStateFromQuery = parseFilterStateFromQueryParam(searchParams);
    dispatch({type: 'RESET_STATE', payload: searchStateFromQuery});
  }, [searchParams]);

  return [state, dispatch];
};

function parseFilterStateFromQueryParam(
  searchParam: URLSearchParams
): SearchState {
  return {
    searchText: [
      ...(searchParam
        .get('dst')
        ?.split('_')
        .map<MuliSelectOption<keyof typeof IconMap>>((value) => ({
          label: value,
          value,
          iconType: 'location',
        })) ?? []),
      ...(searchParam
        .get('text')
        ?.split('_')
        .map<MuliSelectOption<keyof typeof IconMap>>((value) => ({
          label: value,
          value,
          iconType: 'community',
        })) ?? []),
    ],
    bedrooms: searchParam.get('bdr')?.split(',') ?? [],
    bathrooms: searchParam.get('bhr')?.split(',') ?? [],
    price: {
      min: searchParam.get('spr') ?? null,
      max: searchParam.get('mpr') ?? null,
    },
    area: {
      min: searchParam.get('minar') ?? null,
      max: searchParam.get('maxar') ?? null,
    },
    propertyType: searchParam.get('ty')?.split(',') ?? [],
    availability: searchParam.get('cst') ?? 'Any',
    propertyView: searchParam.get('uv') ?? 'Any',
    saleType: searchParam.get('rst') ?? 'Any',
  };
}
export function parseQueryObjectFromQueryParam(searchParam: URLSearchParams) {
  return (
    Object.fromEntries(
      Array.from(searchParam.entries()).filter(([key]) =>
        VALID_SEARCH_PARAMS.includes(key)
      )
    ) ?? {}
  );
}

function Destination() {
  return (
    <svg
      width='20'
      height='21'
      viewBox='0 0 20 21'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'>
      <path
        d='M4.37771 3.99653L3.35449 4.5901C2.53099 5.06781 2.11925 5.30667 1.89287 5.70402C1.6665 6.10138 1.6665 6.58527 1.6665 7.55306V14.3569C1.6665 15.6285 1.6665 16.2643 1.95172 16.6182C2.14151 16.8536 2.40747 17.0119 2.7015 17.0644C3.14339 17.1433 3.6844 16.8294 4.7664 16.2018C5.50114 15.7755 6.20826 15.3329 7.08723 15.4529C7.48705 15.5075 7.86843 15.6971 8.63117 16.0764L11.8094 17.6567C12.4969 17.9985 12.5031 18 13.2676 18H14.9998C16.5712 18 17.3569 18 17.845 17.5011C18.3332 17.0021 18.3332 16.1991 18.3332 14.5931V8.97623C18.3332 7.37019 18.3332 6.56717 17.845 6.06824C17.3569 5.5693 16.5712 5.5693 14.9998 5.5693H13.2676C12.5031 5.5693 12.4969 5.56782 11.8094 5.226L9.03307 3.84553C7.87387 3.26914 7.29427 2.98095 6.67682 3.00098C6.05937 3.02101 5.49881 3.34618 4.37771 3.99653Z'
        stroke='#2D2D2D'
        strokeWidth='1.25'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M6.6665 3L6.6665 15.0833'
        stroke='#2D2D2D'
        strokeWidth='1.25'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M12.5 5.9165L12.5 17.5832'
        stroke='#2D2D2D'
        strokeWidth='1.25'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  );
}

function Penthouse() {
  return (
    <svg
      width='32'
      height='32'
      viewBox='0 0 32 32'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'>
      <path
        d='M2.6665 13.3335L10.8399 13.3335C11.1582 13.3335 11.466 13.2062 11.7077 12.9746L15.1321 9.69242C15.6314 9.21385 16.3682 9.21385 16.8676 9.69242L20.292 12.9746C20.5337 13.2062 20.8415 13.3335 21.1597 13.3335L29.3332 13.3335'
        stroke='#2D2D2D'
        strokeWidth='1.66667'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M4 13.3333L6.61467 7.23243C7.76345 4.55195 8.60051 4 11.5168 4L20.4832 4C23.3995 4 24.2366 4.55195 25.3853 7.23243L28 13.3333'
        stroke='#2D2D2D'
        strokeWidth='1.66667'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M4 13.3335L4 28.0002'
        stroke='#2D2D2D'
        strokeWidth='1.66667'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M28 13.3335L28 28.0002'
        stroke='#2D2D2D'
        strokeWidth='1.66667'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M2.6665 28L29.3332 28'
        stroke='#2D2D2D'
        strokeWidth='1.66667'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M16.0105 14.6665L15.9985 14.6665'
        stroke='#2D2D2D'
        strokeWidth='1.66667'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M12.6665 28L12.6665 22C12.6665 20.8954 13.5619 20 14.6665 20H17.3332C18.4377 20 19.3332 20.8954 19.3332 22V28'
        stroke='#2D2D2D'
        strokeWidth='1.66667'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M8 18.6665H9.33333'
        stroke='#2D2D2D'
        strokeWidth='1.66667'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M22.6665 18.6665L23.9998 18.6665'
        stroke='#2D2D2D'
        strokeWidth='1.66667'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  );
}
function ApartMent() {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='32'
      height='32'
      viewBox='0 0 32 32'
      fill='none'>
      <path
        d='M2.6665 13.3335L9.33317 5.3335M9.33317 5.3335L16.5521 12.5524C16.9375 12.9378 17.1302 13.1305 17.3752 13.232C17.6202 13.3335 17.8927 13.3335 18.4377 13.3335L29.3332 13.3335L24.2657 7.25251C23.4793 6.30885 23.0861 5.83702 22.5486 5.58526C22.0111 5.3335 21.3969 5.3335 20.1685 5.3335L9.33317 5.3335Z'
        stroke='#2D2D2D'
        strokeWidth='1.66667'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M14.6667 10.6665L14.6667 26.6665L4 26.6665L4 11.8094'
        stroke='#2D2D2D'
        strokeWidth='1.66667'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M14.6665 26.6667L27.9998 26.6667L27.9998 12'
        stroke='#2D2D2D'
        strokeWidth='1.66667'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M5.3335 10.0002L5.3335 5.3335'
        stroke='#2D2D2D'
        strokeWidth='1.66667'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M9.34401 16L9.33203 16'
        stroke='#2D2D2D'
        strokeWidth='1.66667'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M9.3335 26.6668L9.3335 21.3335'
        stroke='#2D2D2D'
        strokeWidth='1.66667'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M19.9998 18.6665L22.6665 18.6665'
        stroke='#2D2D2D'
        strokeWidth='1.66667'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  );
}
function SkyTerrace() {
  return (
    <svg
      width='32'
      height='32'
      viewBox='0 0 32 32'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'>
      <path
        d='M9.3335 12L15.6945 6.31352C17.4198 4.77117 18.2824 4 19.3335 4C20.3846 4 21.2472 4.77117 22.9725 6.31352L29.3335 12'
        stroke='#2D2D2D'
        strokeWidth='1.66667'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M11.3335 10.6665L11.3335 26.6665M27.3335 26.6665L27.3335 10.6665'
        stroke='#2D2D2D'
        strokeWidth='1.66667'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <ellipse
        cx='4.6665'
        cy='16.0002'
        rx='2'
        ry='2.66667'
        stroke='#2D2D2D'
        strokeWidth='1.66667'
      />
      <path
        d='M4.6665 18.6665L4.6665 26.6665'
        stroke='#2D2D2D'
        strokeWidth='1.66667'
        strokeLinecap='round'
      />
      <path
        d='M2.6665 26.6665L29.3332 26.6665'
        stroke='#2D2D2D'
        strokeWidth='1.66667'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M16 26.6665L16 21.3328C16 20.076 16 19.4476 16.3905 19.057C16.781 18.6665 17.4096 18.6665 18.6667 18.6665H20C21.2571 18.6665 21.8856 18.6665 22.2761 19.057C22.6667 19.4476 22.6667 20.0761 22.6667 21.3332V26.6665'
        stroke='#2D2D2D'
        strokeWidth='1.66667'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M18 14.6665H20.6667M18 10.6665H20.6667'
        stroke='#2D2D2D'
        strokeWidth='1.66667'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  );
}
function TownHouse() {
  return (
    <svg
      width='32'
      height='32'
      viewBox='0 0 32 32'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'>
      <path
        d='M17.3335 2.6665L2.66683 9.33317'
        stroke='#2D2D2D'
        strokeWidth='1.66667'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M16 4L16 29.3333L9.33333 29.3333C6.81918 29.3333 5.5621 29.3333 4.78105 28.5523C4 27.7712 4 26.5142 4 24L4 9.33333'
        stroke='#2D2D2D'
        strokeWidth='1.66667'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M16 9.3335L29.3333 16.0002'
        stroke='#2D2D2D'
        strokeWidth='1.66667'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M13.3335 29.333H22.6668C25.181 29.333 26.4381 29.333 27.2191 28.552C28.0002 27.7709 28.0002 26.5139 28.0002 23.9997V15.3335'
        stroke='#2D2D2D'
        strokeWidth='1.66667'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M24 13.3335L24 9.3335'
        stroke='#2D2D2D'
        strokeWidth='1.66667'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M9.3335 14.6665H10.6668M9.3335 19.9998H10.6668'
        stroke='#2D2D2D'
        strokeWidth='1.66667'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M21.3335 18.6665H22.6668'
        stroke='#2D2D2D'
        strokeWidth='1.66667'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M22 29.3333V24'
        stroke='#2D2D2D'
        strokeWidth='1.66667'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  );
}
function Villa() {
  return (
    <svg
      width='32'
      height='32'
      viewBox='0 0 32 32'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'>
      <path
        d='M5.3335 29.3335L26.6668 29.3335'
        stroke='#2D2D2D'
        strokeWidth='1.66667'
        strokeLinecap='round'
      />
      <path
        d='M22.6667 12L18.6667 12M24 17.3333H18.6667M24 22.6667H18.6667'
        stroke='#2D2D2D'
        strokeWidth='1.66667'
        strokeLinecap='round'
      />
      <path
        d='M8 29.3332L8 4.26651C8 3.23163 8.62797 2.6665 9.6 2.6665C11.8296 2.6665 12.9444 2.6665 13.8772 2.81424C19.0119 3.6275 23.039 7.65458 23.8523 12.7893C24 13.7221 24 14.8369 24 17.0665L24 29.3332'
        stroke='#2D2D2D'
        strokeWidth='1.66667'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  );
}
function Default() {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='32'
      height='32'
      viewBox='0 0 32 32'
      fill='none'>
      <path
        d='M12.0822 6.43511L4.31882 13.2284C3.65306 13.811 4.09944 14.8457 5.01651 14.8457C5.57679 14.8457 6.03098 15.2652 6.03098 15.7827V20.1074C6.03098 23.828 6.03098 25.6883 7.28235 26.8442C8.53372 28 10.5478 28 14.5759 28H17.4241C21.4522 28 23.4663 28 24.7177 26.8442C25.969 25.6883 25.969 23.828 25.969 20.1074V15.7827C25.969 15.2652 26.4232 14.8457 26.9835 14.8457C27.9006 14.8457 28.3469 13.811 27.6812 13.2284L19.9178 6.43511C18.0626 4.8117 17.135 4 16 4C14.865 4 13.9374 4.8117 12.0822 6.43511Z'
        stroke='#2D2D2D'
        strokeWidth='1.66667'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M16 21.3335H16.012'
        stroke='#2D2D2D'
        strokeWidth='1.66667'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  );
}
